import React, {useEffect, useState} from 'react';
import {Badge, Button} from 'react-bootstrap';
import Configuration from '../config';
import './partner-resource-nvr-status.css';

export function useResourceNvrStatus(token, active) {
  const [state, setState] = useState({fields: {}, error: null});
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!token || !active) return undefined;
    let disposed = false, busy = false;
    let controller = null;
    const load = async () => {
      if (disposed || busy || document.hidden) return;
      busy = true;
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`${Configuration.apiEndPoint}/partners/resources/nvr-status`, {
          headers: {Authorization: `Bearer ${token}`}, signal: controller.signal, cache: 'no-store'
        });
        if (!response.ok) throw new Error('status_unavailable');
        const data = await response.json();
        if (!disposed) setState({fields: Object.fromEntries((data.fields || []).map(field => [field.fieldId, field])), error: null});
      } catch (error) {
        if (!disposed) setState({fields: {}, error: 'Stato temporaneamente non disponibile'});
      } finally {clearTimeout(timeout); busy = false;}
    };
    setState({fields: {}, error: null});
    load();
    const interval = setInterval(load, 30000);
    document.addEventListener('visibilitychange', load);
    return () => {disposed = true; clearInterval(interval); document.removeEventListener('visibilitychange', load); if (controller) controller.abort();};
  }, [token, active, refresh]);
  return {...state, refresh: () => setRefresh(value => value + 1)};
}

const labels = {acquiring: 'Sta registrando', outside_schedule: 'Fuori orario', retry_wait: 'Riconnessione', unknown: 'Stato non disponibile'};
const processNames = {nvr: 'Servizio NVR', recorder: 'Registrazione', uploader: 'Invio video', mediamtx: 'Servizio streaming'};
const combinedProcessState = (current, legacy) => {
  if (current === true || legacy === true) return true;
  if (current === false || legacy === false) return false;
  return undefined;
};
export default function PartnerResourceNvrStatus({status, error, fieldId}) {
  const [details, setDetails] = useState(false);
  const data = status || {};
  const fresh = data.lastSeenAt && Date.now() - Date.parse(data.lastSeenAt) < 120000;
  const online = data.online === true && fresh;
  const known = typeof data.online === 'boolean';
  const cameras = data.cameras || [];
  const reportedProcesses = data.processes || {};
  const processes = {...reportedProcesses,
    nvr: combinedProcessState(reportedProcesses.nvr, reportedProcesses.legacyNvr),
    uploader: combinedProcessState(reportedProcesses.uploader, reportedProcesses.legacyUploader)
  };
  const stateLabel = error || (online ? 'NVR online' : known ? 'NVR offline' : 'Stato non disponibile');
  const detailId = `resource-nvr-details-${data.fieldId || fieldId}`;
  return <section className="partner-nvr-state" aria-label="Stato registrazione del campo">
    <div className="partner-nvr-state__heading"><strong>Stato registrazione</strong><Badge variant={online ? 'success' : known && !error ? 'danger' : 'secondary'}>{stateLabel}</Badge></div>
    <div className="partner-nvr-state__operations">
      {online && <><span>Registrazione: {processes.recorder === true ? 'servizio attivo' : 'vedi telecamere'}</span><span>Invio video: {processes.uploader === true ? 'operativo' : 'non disponibile'}</span></>}
      {data.legacyAnomaly && online && <span>Segnalazione di anomalia NVR</span>}
    </div>
    {cameras.map((camera, index) => <div className="partner-nvr-state__camera" key={camera.key}><span>{camera.name || `Telecamera ${index + 1}`}</span><Badge variant={online && camera.state === 'acquiring' ? 'success' : online && camera.state === 'retry_wait' ? 'warning' : 'secondary'}>{online ? labels[camera.state] || labels.unknown : known ? 'Stato non aggiornato' : labels.unknown}</Badge></div>)}
    <div className="partner-nvr-state__footer"><span>{data.lastSeenAt ? `Ultimo contatto: ${new Date(data.lastSeenAt).toLocaleString()}` : 'In attesa di informazioni'}</span><Button variant="link" size="sm" aria-expanded={details} aria-controls={detailId} onClick={() => setDetails(value => !value)}>{details ? 'Nascondi dettagli' : 'Mostra dettagli'}</Button></div>
    {details && <div id={detailId}>{Object.keys(processNames).map(key => <div className="partner-nvr-state__camera" key={key}><span>{processNames[key]}</span><span>{!online ? 'Stato non aggiornato' : processes[key] === true ? 'Attivo' : processes[key] === false ? 'Non attivo' : 'Non disponibile'}</span></div>)}</div>}
  </section>;
}
